import React, { MouseEvent, useRef, useEffect } from 'react';

import { observer } from 'mobx-react-lite';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import {
    Container,
    Section,
    SectionTitle,
    PlansGradient,
    LegalLinks,
    Accordion,
} from 'components';

import analytics from 'tracking/analytics';

import { PATHS, URLS, ACCESS } from 'config';
import { PLANS_LIST__BUTTON__SELECT_PLAN } from 'tracking/trackingData';

import { StyledLink, StyledReactLink } from 'components/StyledCommon';
import { sendBlackFridayPageView } from 'tracking/sendTrackingEvent';

import * as help from 'helpers';
import {
    DesktopHero,
    SectionPlans,
    ColumnHeaders,
    ColumnPlans,
    SectionConversion,
    LegalLinksConversion,
} from './styled';
import {
    ExistingUsers,
    NewUsers,
    FamilyOnlyUsers,
    ExistingUsersConversion,
    NewUsersConversion,
    FamilyOnlyUsersConversion,
} from './headers';
import {
    ExistingUsersSections,
    NewUsersSections,
    FamilyOnlyUsersSections,
    NonLicensedSections,
} from './sections';
import { Ratings } from './ratings';

type UserType = 'new' | 'old' | 'family';

const campaignEnds = ACCESS['black-friday'].campaign_ends;

const getHeaders = (
    type: UserType,
    licensedContent: boolean,
    onClick: () => void,
) => {
    const props = { campaignEnds, onClick };

    if (!licensedContent) return <NewUsers {...props} />;

    switch (type) {
        case 'new':
            return <NewUsers {...props} />;
        case 'old':
            return <ExistingUsers {...props} />;
        case 'family':
            return <FamilyOnlyUsers {...props} />;
    }
};

const getHeadersSectionConversion = (type: UserType) => {
    switch (type) {
        case 'new':
            return <NewUsersConversion campaignEnds={campaignEnds} />;
        case 'old':
            return <ExistingUsersConversion campaignEnds={campaignEnds} />;
        case 'family':
            return <FamilyOnlyUsersConversion campaignEnds={campaignEnds} />;
    }
};

const getSections = (type: UserType, licensedContent: boolean) => {
    if (!licensedContent) return <NonLicensedSections />;

    switch (type) {
        case 'new':
            return <NewUsersSections />;
        case 'old':
            return <ExistingUsersSections />;
        case 'family':
            return <FamilyOnlyUsersSections />;
    }
};

const mapParams = (params: { [key: string]: string | string[] | null }) => {
    const version = params.v;
    const noFamily = params.f === 'false';
    const res: { userType: UserType; noFamily: boolean } = {
        userType: 'new',
        noFamily,
    };

    if (version === 'o') res.userType = 'old';
    if (version === 'f') {
        res.userType = 'family';
        res.noFamily = false;
    }

    return res;
};

const Page = () => {
    let { userType, noFamily } = mapParams(help.useQueryParams());
    const { plans, licensedContent } = help.useGuardBlackFriday(
        noFamily,
        userType,
    );

    // switch userType if there is only family plan
    if (plans.every((p) => p.family)) userType = 'family';

    const scrollRef = useRef<HTMLDivElement>(null);

    const onLearnMoreClick = () => {
        if (scrollRef.current) {
            window.scrollTo({
                behavior: 'smooth',
                top: scrollRef.current.offsetTop - help.getNavBarHeight(),
            });
        }
    };

    const onPlanClick = (e: MouseEvent<HTMLDivElement>, plan: any) => {
        e && e.preventDefault();

        const context = plans.map((p) => p.id).join(',');

        analytics.trackEvent({
            ...PLANS_LIST__BUTTON__SELECT_PLAN,
            context: context,
            name: plan.id,
        });

        if (!plan.has_all_instruments) {
            let url = help.route_with_params(PATHS.blackFridayCheckout, {
                id: plan.id,
            });

            help.goto(PATHS.selectInstrument, {
                continue: url,
            });
        } else {
            help.goto(PATHS.blackFridayCheckout, {
                id: plan.id,
            });
        }
    };

    help.useEffectOnlyOnce(() => {
        plans && plans.length && sendBlackFridayPageView();
    });

    const { elementRef, isOnscreen } = help.useElementOnScreen();
    const { setWidgetVisible } = help.useZendeskWidget();

    useEffect(() => {
        setWidgetVisible(isOnscreen);
    }, [setWidgetVisible, isOnscreen]);

    return plans && plans.length ? (
        <React.Fragment>
            <DesktopHero $isFamily={userType === 'family'}>
                <SectionPlans>
                    <ColumnHeaders>
                        {getHeaders(
                            userType,
                            licensedContent,
                            onLearnMoreClick,
                        )}
                    </ColumnHeaders>
                    <ColumnPlans>
                        <PlansGradient
                            biggerCta
                            showToggle={false}
                            plans={plans}
                            onPlanClick={onPlanClick}
                            planCta={i18n._(t`Get your deal now`)}
                            featuresType="blackFriday"
                        />

                        <LegalLinks
                            theme={help.isTabletOrSmaller() ? 'dark' : 'light'}
                        />
                    </ColumnPlans>
                </SectionPlans>
            </DesktopHero>

            <div ref={elementRef}>
                <div ref={scrollRef}>
                    {getSections(userType, licensedContent)}
                </div>

                {licensedContent && (
                    <SectionConversion>
                        <Container>
                            {getHeadersSectionConversion(userType)}
                            <PlansGradient
                                biggerCta
                                showToggle={false}
                                plans={plans}
                                onPlanClick={onPlanClick}
                                planCta={i18n._(t`Get your deal now`)}
                                featuresType="blackFriday"
                            />
                        </Container>

                        <LegalLinksConversion />
                    </SectionConversion>
                )}

                {(userType === 'new' || !licensedContent) && <Ratings />}

                <Section background="white">
                    <Container>
                        <SectionTitle
                            align="center"
                            title={i18n._(t`Frequently asked questions`)}
                        />

                        <Accordion>
                            <div data-label={i18n._(t`What is Yousician?`)}>
                                <Trans>
                                    As the largest music educator in the world,
                                    Yousician has helped over 25 million people
                                    in 154 countries learn guitar, bass, piano,
                                    ukulele, and vocals. Boasting thousands of
                                    teacher-crafted lessons, exercises, and
                                    songs, Yousician makes achieving your
                                    musical goals manageable and fun. Thanks to
                                    award-winning audio technology and
                                    world-class educators, Yousician provides
                                    meaningful, real-time feedback as you play.
                                </Trans>
                            </div>

                            {licensedContent ? (
                                <div
                                    data-label={i18n._(
                                        t`What is Yousician Premium+?`,
                                    )}
                                >
                                    <Trans>
                                        Premium+ unlocks everything Yousician
                                        has to offer. Guided lessons, access to
                                        our entire library of thousands of
                                        songs, exercises, and teacher-crafted
                                        lessons. You also get instant access to
                                        our full instrument offering, allowing
                                        you to jump between instruments as you
                                        please.
                                    </Trans>
                                </div>
                            ) : (
                                <div
                                    data-label={i18n._(
                                        t`What is Yousician Premium All?`,
                                    )}
                                >
                                    <Trans>
                                        Premium All unlocks everything Yousician
                                        has to offer. Guided lessons, access to
                                        our entire library of thousands of
                                        songs, exercises, and teacher-crafted
                                        lessons. You also get instant access to
                                        our full instrument offering, allowing
                                        you to jump between instruments as you
                                        please.
                                    </Trans>
                                </div>
                            )}

                            {plans.some((p) => p.family) && (
                                <div
                                    data-label={i18n._(
                                        t`What's included in a Family Plan membership?`,
                                    )}
                                >
                                    <Trans>
                                        Unlimited access to all 5 instruments
                                        (guitar, piano, ukulele, bass and
                                        singing) and content for yourself and up
                                        to 3 other family members. Each family
                                        member has their own Yousician account
                                        and can track their progress
                                        individually. Family members can save
                                        songs as favorites and create their own
                                        song collections for their accounts.
                                    </Trans>
                                </div>
                            )}

                            {plans.some((p) => p.family) && (
                                <div
                                    data-label={i18n._(
                                        t`How easy is it to add Family Plan members?`,
                                    )}
                                >
                                    <Trans>
                                        You can add family plan members in 2
                                        easy steps! After subscribing to Family
                                        plan, go to your{' '}
                                        <StyledReactLink
                                            $color="brand"
                                            $size="bodyl"
                                            to={PATHS.dashboard}
                                        >
                                            account page
                                        </StyledReactLink>
                                        , scroll down to the{' '}
                                        <b>Family members</b> section and select
                                        the <b>Plus</b> button. Then, add the
                                        Yousician account info of a family
                                        member you live with. Once you add a
                                        family member, they receive an email
                                        notification that they've been added to
                                        your Family membership.
                                    </Trans>
                                </div>
                            )}

                            {plans.some((p) => p.family) && (
                                <div
                                    data-label={i18n._(
                                        t`How does the payment for Family plan work? Will I pay for all 4 accounts?`,
                                    )}
                                >
                                    <Trans>
                                        The Family plan{' '}
                                        <b>account manager makes the payment</b>{' '}
                                        for each billing period. This can be
                                        either monthly or annually, depending on
                                        the chosen plan. The account manager can
                                        add family members to join their band,
                                        remove members and manage the
                                        subscription at any time through their
                                        account page on{' '}
                                        <StyledReactLink
                                            $color="brand"
                                            $size="bodyl"
                                            to={PATHS.dashboard}
                                        >
                                            our website.
                                        </StyledReactLink>
                                    </Trans>
                                </div>
                            )}

                            <div
                                data-label={i18n._(
                                    t`How does the discount work?`,
                                )}
                            >
                                <Trans>
                                    The discount is applied only for the first
                                    year. Your contract will renew automatically
                                    at the regular price on your yearly renewal
                                    date, until you cancel your subscription.
                                    You can cancel the renewal of your
                                    subscription by logging into your Yousician
                                    account{' '}
                                    <StyledLink
                                        $size="bodyl"
                                        $color="brand"
                                        href={'/login'}
                                    >
                                        here
                                    </StyledLink>
                                    .
                                </Trans>
                            </div>

                            <div
                                data-label={i18n._(
                                    t`Am I eligible for the offer?`,
                                )}
                            >
                                <Trans>
                                    All users who don't have an ongoing
                                    subscription are eligible for the offer. If
                                    you have an ongoing subscription with
                                    Yousician (with either iOS, Google Play,
                                    card or PayPal) you might not be eligible
                                    for the offer.
                                </Trans>
                            </div>

                            {!licensedContent && (
                                <div
                                    data-label={i18n._(
                                        t`What plans can I purchase with the discount?`,
                                    )}
                                >
                                    <Trans>
                                        The offer is available for yearly
                                        Premium All subscriptions only and does
                                        not apply for other premium options.
                                    </Trans>
                                </div>
                            )}

                            <div data-label={i18n._(t`Other questions`)}>
                                <Trans>
                                    Feel free to reach out to us{' '}
                                    <StyledLink
                                        $size="bodyl"
                                        $color="brand"
                                        href={URLS.supportNew}
                                    >
                                        here
                                    </StyledLink>
                                    . Our Customer Support team is always happy
                                    to help &lt;3
                                </Trans>
                            </div>
                        </Accordion>
                    </Container>
                </Section>
            </div>
        </React.Fragment>
    ) : null;
};

/**
 * Name of the access campaign. This is being
 * used in order to display the login
 * page with benefits.
 */
Page.accessCampaign = 'black-friday';

export default observer(Page);
